/**
 * Pure helpers for reading user-selected image files as data URLs.
 *
 * Extracted from `useImageDropZone` so non-hook callers (e.g. file-input
 * change handlers in `useImageSelection`, `useRefineUpload`) can share one
 * implementation instead of re-spreading `FileReader` boilerplate.
 */

import type { ChangeEvent } from "react";

/**
 * Mirrors `DECODE_MAX_EDGE` in the backend's `utils/helper.py`: every upload is
 * decoded down to this long edge before the model sees it, so pixels past it
 * only cost upload time and push files over the server's size cap.
 */
export const MAX_IMAGE_EDGE = 2048;

const EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

/** Dimensions that fit within `maxEdge`, or null if the image already does. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = MAX_IMAGE_EDGE,
): { width: number; height: number } | null {
  const scale = maxEdge / Math.max(width, height);
  if (scale >= 1) return null;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Scale an image whose long edge exceeds `MAX_IMAGE_EDGE` down to it,
 * re-encoding in the original format where the browser can (Safari cannot
 * encode WebP and hands back PNG). Anything the browser cannot decode is
 * returned untouched, so the server stays the one to reject it.
 */
export async function downscaleImageFile(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const target = fitWithin(bitmap.width, bitmap.height);
    if (!target) return file;

    const canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, file.type, 0.92),
    );
    if (!blob) return file;
    const extension = EXTENSIONS[blob.type] ?? "png";
    const name = `${file.name.replace(/\.[^.]*$/, "")}.${extension}`;
    return new File([blob], name, {
      type: blob.type,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
}

/**
 * Downscale a `File` if it is oversized, read it asynchronously, and invoke
 * `onImageSelect` with the resulting `data:` URL and the file to upload.
 * No React dependencies.
 */
export function readFileAsDataUrl(
  file: File,
  onImageSelect: (dataUrl: string, file: File) => void,
): void {
  downscaleImageFile(file).then((scaled) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      onImageSelect(dataUrl, scaled);
    };
    reader.readAsDataURL(scaled);
  });
}

/** Promise form of {@link readFileAsDataUrl} for callers that attach the
 *  image only after another step (naming it, saving it) has finished. Uses
 *  the browser's native async encoding so large files do not block the UI
 *  thread with manual byte-by-byte Base64. */
export function readFileAsDataUrlAsync(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/** The file's own name is the obvious first suggestion; the extension is noise
 *  in a library listing. */
export function fileNameStem(fileName: string): string {
  return fileName.replace(/\.[^./\\]+$/, "");
}

/**
 * Change handler for a hidden `<input type="file">`: hands the selected file
 * to `onFile` (typically `useImageIntake`) and re-arms the input so picking
 * the same file twice still fires `change`.
 */
export function readFileInputChange(
  event: ChangeEvent<HTMLInputElement>,
  onFile: (file: File) => void,
): void {
  const file = event.target.files?.[0];
  if (file) onFile(file);
  event.target.value = "";
}
